import { useEffect } from "react";
import { Stack, TextField, FormControlLabel, Switch, MenuItem } from "@mui/material";
import { useField } from "formik";

export type SrtInputParamsFormValues = {
  latency: number | "";
  passphrase: string;
  pbkeylen: number | "";
  streamid: string;
  transtype: string;
  blocking: boolean;
};

export const createDefaultSrtInputParams = (): SrtInputParamsFormValues => ({
  latency: 2000,
  passphrase: "",
  pbkeylen: "",
  streamid: "",
  transtype: "live",
  blocking: true
});

type SrtInputParamsProps = {
  name?: string;
  mode?: "view" | "edit";
};

const SrtInputParams = ({ name = "srt", mode = "edit" }: SrtInputParamsProps) => {
  const readOnly = mode === "view";

  const [latencyField, latencyMeta, latencyHelpers] = useField<number | "">({
    name: `${name}.latency`
  });
  const [passphraseField, passphraseMeta] = useField<string>({ name: `${name}.passphrase` });
  const [pbkeylenField, pbkeylenMeta, pbkeylenHelpers] = useField<number | "">({
    name: `${name}.pbkeylen`
  });
  const [streamIdField, streamIdMeta] = useField<string>({ name: `${name}.streamid` });
  const [transtypeField, transtypeMeta] = useField<string>({ name: `${name}.transtype` });
  const [blockingField, , blockingHelpers] = useField<boolean>({ name: `${name}.blocking` });

  useEffect(() => {
    if (readOnly) {
      return;
    }
    const hasPassphrase = Boolean(passphraseField.value?.trim());
    if (!hasPassphrase && pbkeylenField.value !== "") {
      pbkeylenHelpers.setValue("");
      return;
    }
    if (hasPassphrase && pbkeylenField.value === "") {
      pbkeylenHelpers.setValue(16);
    }
  }, [readOnly, passphraseField.value, pbkeylenField.value, pbkeylenHelpers]);

  return (
    <Stack spacing={2}>
      <TextField
        {...latencyField}
        label="Latency (ms)"
        type="number"
        value={latencyField.value}
        onChange={(event) => {
          const nextValue = event.target.value;
          latencyHelpers.setValue(nextValue === "" ? "" : Number(nextValue));
        }}
        error={Boolean(latencyMeta.touched && latencyMeta.error)}
        helperText={latencyMeta.touched ? latencyMeta.error : undefined}
        disabled={readOnly}
        slotProps={{ htmlInput: { min: 0, step: 1 } }}
        fullWidth
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          {...passphraseField}
          label="Passphrase"
          value={passphraseField.value}
          error={Boolean(passphraseMeta.touched && passphraseMeta.error)}
          helperText={passphraseMeta.touched ? passphraseMeta.error : undefined}
          disabled={readOnly}
          fullWidth
          sx={{ flexBasis: { sm: "70%" }, flexGrow: 1 }}
        />
        {passphraseField.value?.trim() ? (
          <TextField
            {...pbkeylenField}
            label="Public key len"
            select
            value={pbkeylenField.value === "" ? "" : pbkeylenField.value}
            onChange={(event) => {
              const nextValue = event.target.value;
              pbkeylenHelpers.setValue(nextValue === "" ? "" : Number(nextValue));
            }}
            error={Boolean(pbkeylenMeta.touched && pbkeylenMeta.error)}
            helperText={pbkeylenMeta.touched ? pbkeylenMeta.error : undefined}
            disabled={readOnly}
            fullWidth
            sx={{ flexBasis: { sm: "30%" }, maxWidth: { sm: "30%" } }}
          >
            <MenuItem value={16}>16 (AES-128)</MenuItem>
            <MenuItem value={24}>24 (AES-192)</MenuItem>
            <MenuItem value={32}>32 (AES-256)</MenuItem>
          </TextField>
        ) : null}
      </Stack>
      <TextField
        {...streamIdField}
        label="Stream ID"
        value={streamIdField.value}
        error={Boolean(streamIdMeta.touched && streamIdMeta.error)}
        helperText={streamIdMeta.touched ? streamIdMeta.error : undefined}
        disabled={readOnly}
        fullWidth
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
        <TextField
          {...transtypeField}
          label="Transport Type"
          select
          value={transtypeField.value}
          error={Boolean(transtypeMeta.touched && transtypeMeta.error)}
          helperText={transtypeMeta.touched ? transtypeMeta.error : undefined}
          disabled
          fullWidth
          sx={{ flexBasis: { sm: "70%" }, flexGrow: 1 }}
        >
          <MenuItem value="live">Live</MenuItem>
          <MenuItem value="file">File</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={blockingField.value}
              onChange={(event) => blockingHelpers.setValue(event.target.checked)}
              name={blockingField.name}
              disabled
            />
          }
          label="Blocking"
          sx={{ flexBasis: { sm: "30%" }, m: 0 }}
        />
      </Stack>
    </Stack>
  );
};

export default SrtInputParams;
